import { useState, useEffect, useCallback } from 'react';
import { API_URL } from '@/lib/api-client';
const AUTH_MODE = import.meta.env.VITE_AUTH_MODE || 'auth';

const DEMO_USER_ID = '00000000-0000-0000-0000-000000000001';

export interface AuthUser {
  id: string;
  email: string;
  displayName: string | null;
  status: string;
  adminRole: string | null;
}

export function useAuth() {
  const [user, setUser] = useState<AuthUser | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const isDemo = AUTH_MODE === 'demo';

  const checkAuth = useCallback(async () => {
    if (isDemo) {
      setUser({
        id: DEMO_USER_ID,
        email: 'demo@levelupcoach.app',
        displayName: 'Demo User',
        status: 'active',
        adminRole: 'super_admin',
      });
      setIsLoading(false);
      return;
    }

    try {
      const res = await fetch(`${API_URL}/api/auth/me`, {
        credentials: 'include',
      });
      if (res.ok) {
        const data = await res.json();
        setUser(data.user);
      } else {
        setUser(null);
      }
    } catch {
      setUser(null);
    } finally {
      setIsLoading(false);
    }
  }, [isDemo]);

  useEffect(() => {
    checkAuth();
  }, [checkAuth]);

  const signInWithEmail = useCallback(async (email: string, password: string) => {
    setIsLoading(true);
    setError(null);
    try {
      const res = await fetch(`${API_URL}/api/auth/login`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({ email, password }),
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.error || 'Login failed');
      setUser(data.user);
      return data;
    } catch (err) {
      const message = err instanceof Error ? err.message : 'Login failed';
      setError(message);
      throw err;
    } finally {
      setIsLoading(false);
    }
  }, []);

  const signUpWithEmail = useCallback(async (email: string, password: string, displayName?: string) => {
    setIsLoading(true);
    setError(null);
    try {
      const res = await fetch(`${API_URL}/api/auth/signup`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({ email, password, displayName }),
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.error || 'Registration failed');
      if (!data.requiresApproval) {
        setUser(data.user);
      }
      return data;
    } catch (err) {
      const message = err instanceof Error ? err.message : 'Registration failed';
      setError(message);
      throw err;
    } finally {
      setIsLoading(false);
    }
  }, []);

  const signOut = useCallback(async () => {
    if (isDemo) return;
    try {
      await fetch(`${API_URL}/api/auth/logout`, {
        method: 'POST',
        credentials: 'include',
      });
    } finally {
      setUser(null);
    }
  }, [isDemo]);

  const clearError = useCallback(() => setError(null), []);

  return {
    user,
    isLoading,
    isDemo,
    error,
    checkAuth,
    signInWithEmail,
    signUpWithEmail,
    signOut,
    clearError,
  };
}
